/**
 * Lineage-style professions (subclass choice at a level gate).
 * Hybrid skill trees: free rank-1 by level; SP for branches, passives, ranks 2+.
 * Late nodes also require cumulative SP already spent (investment gate).
 * Inside a profession both branches stay open. The only permanent forks are the
 * profession at level 10 and the senior profession at level 40.
 */
import { ClassId, Race } from "../sim/game-types.js";
import { isClassAllowedForRace } from "../sim/appearance.js";
import type { AbilityEffect, AbilityPresentation } from "./ability-def.js";
import type { CombatKitId } from "../sim/combat-kit.js";

export const ProfessionId = {
  None: 0,
  /** Mage → Маг воды (dark elf: Заклинатель бури) */
  WaterMage: 1,
  /** Mage → Пиромант (dark elf: Маг Бездны, orc: Шаман пламени) */
  Pyromancer: 2,
  /** Mage → Шаман камня (orc) */
  EarthMage: 3,
  /** Warrior */
  Gladiator: 10,
  Knight: 11,
  /** Ranger */
  Scout: 20,
  Marksman: 21,
  /** Priest */
  Cleric: 30,
  Bishop: 31,
} as const;
export type ProfessionId = (typeof ProfessionId)[keyof typeof ProfessionId];

/** First profession choice (L2-style early job change). */
export const PROFESSION_UNLOCK_LEVEL = 10;

/** Senior profession gate (character level). */
export const PROFESSION_ADVANCE_LEVEL = 40;

/**
 * SP that must already be spent in the level-10 tree before the senior path opens.
 * Milestones 10…35 pay 6 SP, so the hero must have invested the whole early budget.
 * Catalog `advanceSpRequired` overrides it.
 */
export const PROFESSION_ADVANCE_SP_REQUIRED = 6;

export type ProfessionTier = 1 | 2;

/** Passive grouping in the skills panel summary. */
export type PassiveFocus = "offense" | "guard" | "tempo" | "utility";

export interface ProfessionAdvanceRaceVariant {
  raceIds: number[];
  label: string;
  hook: string;
}

export interface ProfessionAdvancePreview {
  id: number;
  label: string;
  /** One sentence: what this senior path becomes. */
  hook: string;
  raceVariants?: ProfessionAdvanceRaceVariant[];
}

/** Senior path preview as this race sees it. */
export function resolveAdvancePreview(
  advance: ProfessionAdvancePreview,
  race: number,
): { id: number; label: string; hook: string } {
  const v = advance.raceVariants?.find((x) => x.raceIds.includes(race));
  return { id: advance.id, label: v?.label ?? advance.label, hook: v?.hook ?? advance.hook };
}

/** SP cost per rank upgrade (rank 2, 3, …). */
export const ABILITY_RANK_UP_SP_COST = 1;

export type SkillTreeNodeKind = "active" | "passive";

/**
 * One node in a profession skill tree.
 * No character-level gate. R1 is free when spCost=0; SP otherwise.
 * Learning / ranking requires skillSpSpent ≥ skillTreeReqSpentSp(unlockLevel).
 */
export interface SkillTreeNodeDef {
  id: string;
  abilityId: string;
  /** Grid column for UI (0-based). */
  column: number;
  /** Grid row for UI (0-based). */
  row: number;
  /** All of these node ids must be learned. */
  requires?: string[];
  /** At least one of these node ids must be learned (in addition to requires). */
  requiresAny?: string[];
  /**
   * Place in the book: sort order and the spent-SP band (skillTreeReqSpentSp).
   * Not a character-level requirement.
   */
  unlockLevel: number;
  /**
   * SP to learn rank 1 (0 = free).
   * Rank upgrades always cost ABILITY_RANK_UP_SP_COST.
   */
  spCost: number;
  /**
   * Issued at rank 1 when the profession is chosen. Opening passive the combat feel
   * depends on. Does not spend SP.
   */
  grantOnPick?: boolean;
  /** Mutually exclusive group (e.g. "fork"). */
  exclusiveGroup?: string;
  /** Branch key inside exclusiveGroup (e.g. "tide" | "frost"). */
  exclusiveKey?: string;
  kind: SkillTreeNodeKind;
  /**
   * Passive summary group. Actives omit it.
   * The panel draws passives on their own track; column/row stay for the class book.
   */
  focus?: PassiveFocus;
  /** Capstone / signature visual accent in UI. */
  capstone?: boolean;
  /**
   * Restrict this node to specific races (e.g. dwarf-only craft-mastery passives).
   * Omit = visible/learnable by any race that has the profession.
   */
  raceIds?: number[];
}

/** True when `node` is visible/learnable for `race` (raceIds gate, if any). */
export function skillTreeNodeVisibleForRace(node: SkillTreeNodeDef, race: number): boolean {
  if (!node.raceIds?.length) return true;
  return node.raceIds.includes(race);
}

export interface ProfessionRaceVariant {
  raceIds: number[];
  label: string;
  description?: string;
  /** Weapon kit for this race flavor (overrides the entry kit). */
  kit?: CombatKitId;
  /** Replace book list when set */
  abilityIds?: string[];
  /**
   * Per-ability effect overrides for this race (e.g. different buff id).
   * Keys = ability id.
   */
  abilityOverrides?: Record<
    string,
    {
      label?: string;
      description?: string;
      effectText?: string;
      effects?: AbilityEffect[];
      presentation?: AbilityPresentation;
    }
  >;
}

export interface ProfessionOptionDef {
  id: ProfessionId | number;
  /** Required base class */
  classId: ClassId;
  /** 1 = level-10 profession. 2 = senior path, not offered until its trial exists. */
  tier?: ProfessionTier;
  /** Base profession this senior path belongs to. */
  parentId?: number;
  /** Level gate for this entry. Base professions use the catalog unlock. */
  unlockLevel?: number;
  /** Races that may take this profession. Omit = every race allowed for the class. */
  raceIds?: number[];
  label: string;
  description: string;
  /** Two senior paths shown from level 10. No abilities until the trial exists. */
  advances?: ProfessionAdvancePreview[];
  /**
   * Skill-book ability ids (must be learned before cast).
   * Derived from `tree` when present; kept for backward compatibility.
   */
  abilityIds: string[];
  /** Hybrid skill tree layout (preferred). */
  tree?: SkillTreeNodeDef[];
  /** Tree not designed yet (placeholder nodes): check-skill-trees skips the capstone rule. */
  draft?: boolean;
  /** Optional race flavors */
  raceVariants?: ProfessionRaceVariant[];
  /** Weapon kit; omit = class default kit. */
  kit?: CombatKitId;
}

export interface ProfessionCatalog {
  schemaVersion: 1;
  unlockLevel: number;
  /** Senior path level gate (default PROFESSION_ADVANCE_LEVEL). */
  advanceLevel?: number;
  /** SP already spent in the base tree to open the senior path. */
  advanceSpRequired?: number;
  entries: ProfessionOptionDef[];
}

export function emptyProfessionCatalog(): ProfessionCatalog {
  return {
    schemaVersion: 1,
    unlockLevel: PROFESSION_UNLOCK_LEVEL,
    advanceLevel: PROFESSION_ADVANCE_LEVEL,
    advanceSpRequired: PROFESSION_ADVANCE_SP_REQUIRED,
    entries: [],
  };
}

/**
 * Base tree followed by the senior tree. Senior nodes may `requires` base node ids.
 * Returns the base array itself when there is no senior tree (no alloc).
 */
export function mergeProfessionTrees(
  base: SkillTreeNodeDef[] | undefined,
  senior: SkillTreeNodeDef[] | undefined,
): SkillTreeNodeDef[] | undefined {
  if (!senior?.length) return base;
  if (!base?.length) return senior;
  return [...base, ...senior];
}

/** Profession shown in HUD, lists and tooltips: the senior path once chosen. */
export function displayProfessionId(professionId: number, advancedProfessionId = 0): number {
  return (advancedProfessionId | 0) > 0 ? advancedProfessionId | 0 : professionId | 0;
}

/** Why the senior path is closed, or null when it may be chosen. */
export function professionAdvanceBlock(opts: {
  level: number;
  spSpent: number;
  advanceLevel: number;
  spRequired: number;
}): string | null {
  if (opts.level < opts.advanceLevel) return `Нужен ${opts.advanceLevel} уровень`;
  if (opts.spSpent < opts.spRequired) {
    return `Вложите ${opts.spRequired} SP в умения профессии (сейчас ${opts.spSpent})`;
  }
  return null;
}

/** Flatten unique ability ids from a tree (stable order by column,row). */
export function abilityIdsFromTree(tree: SkillTreeNodeDef[] | undefined): string[] {
  return abilityIdsVisibleForRace(tree);
}

/**
 * Ability ids of nodes this race can learn.
 * Omit `race` to keep every node (catalog storage). A race skips nodes gated to other races.
 */
export function abilityIdsVisibleForRace(
  tree: SkillTreeNodeDef[] | undefined,
  race?: number,
): string[] {
  if (!tree?.length) return [];
  const sorted = [...tree].sort((a, b) => a.column - b.column || a.row - b.row);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const n of sorted) {
    if (race != null && !skillTreeNodeVisibleForRace(n, race)) continue;
    const id = String(n.abilityId || "").trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Player-facing profession names that own this node.
 * Race-gated nodes use the matching race-variant label (Снайпер / Тёмный лучник), not the entry title.
 */
export function professionLabelsForNode(
  entry: Pick<ProfessionOptionDef, "label" | "raceVariants">,
  node: Pick<SkillTreeNodeDef, "raceIds">,
): string[] {
  const races = node.raceIds ?? [];
  const variants = entry.raceVariants ?? [];
  if (!races.length || !variants.length) return entry.label ? [entry.label] : [];
  const labels: string[] = [];
  const seen = new Set<string>();
  for (const variant of variants) {
    if (!variant.raceIds.some((race) => races.includes(race))) continue;
    const label = variant.label?.trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels.length ? labels : entry.label ? [entry.label] : [];
}

export function normalizeProfessionEntry(entry: ProfessionOptionDef): ProfessionOptionDef {
  const tree = Array.isArray(entry.tree) ? entry.tree : undefined;
  const fromTree = abilityIdsFromTree(tree);
  const abilityIds =
    fromTree.length > 0
      ? fromTree
      : Array.isArray(entry.abilityIds)
        ? entry.abilityIds.map((x) => String(x))
        : [];
  return { ...entry, tree, abilityIds };
}

export function normalizeProfessionCatalog(raw: ProfessionCatalog): ProfessionCatalog {
  return {
    schemaVersion: 1,
    unlockLevel: Math.max(1, Number(raw.unlockLevel) || PROFESSION_UNLOCK_LEVEL),
    advanceLevel: Math.max(1, Number(raw.advanceLevel) || PROFESSION_ADVANCE_LEVEL),
    advanceSpRequired: Math.max(
      0,
      Number.isFinite(Number(raw.advanceSpRequired))
        ? Math.floor(Number(raw.advanceSpRequired))
        : PROFESSION_ADVANCE_SP_REQUIRED,
    ),
    entries: (raw.entries ?? []).map((e) => normalizeProfessionEntry(e)),
  };
}

/**
 * Tree node of `abilityId`. The same ability may sit in nodes of several races; with `race`
 * the node visible to that race wins.
 */
export function findTreeNode(
  tree: SkillTreeNodeDef[] | undefined,
  abilityId: string,
  race?: number,
): SkillTreeNodeDef | undefined {
  if (!tree?.length) return undefined;
  const id = String(abilityId || "").trim();
  if (race != null) {
    const own = tree.find((n) => n.abilityId === id && skillTreeNodeVisibleForRace(n, race));
    if (own) return own;
  }
  return tree.find((n) => n.abilityId === id);
}

export function findTreeNodeById(
  tree: SkillTreeNodeDef[] | undefined,
  nodeId: string,
): SkillTreeNodeDef | undefined {
  if (!tree?.length) return undefined;
  return tree.find((n) => n.id === nodeId);
}

/** SP backlog when choosing a profession at `level` (inclusive unlock level). */
export function skillSpBacklog(level: number, unlockLevel = PROFESSION_UNLOCK_LEVEL): number {
  const L = Math.max(1, Math.floor(level) || 1);
  const U = Math.max(1, Math.floor(unlockLevel) || PROFESSION_UNLOCK_LEVEL);
  return Math.max(0, L - U + 1);
}

export function skillSpAvailable(earned: number, spent: number): number {
  return Math.max(0, (earned | 0) - (spent | 0));
}

/**
 * Cumulative SP that must already be spent before learning / ranking a node.
 * Bands by the node's unlockLevel (not character level):
 *   1–19 → 0, 20–29 → 1, 30–39 → 2, 40+ → 3.
 * Stops a high-level character from taking late free R1 / ranks without investing.
 */
export function skillTreeReqSpentSp(unlockLevel: number): number {
  const L = Math.max(1, unlockLevel | 0);
  if (L < 20) return 0;
  if (L < 30) return 1;
  if (L < 40) return 2;
  return 3;
}

/** Class gate plus the profession's own raceIds gate. */
export function professionAllowedForRace(
  def: { classId: number; raceIds?: number[] },
  race: number,
): boolean {
  if (!isClassAllowedForRace(race as Race, def.classId as ClassId)) return false;
  if (!def.raceIds?.length) return true;
  return def.raceIds.includes(race);
}

/**
 * Icon file stem under icons/hud/profession/: `<id>_<race>` when the entry has a
 * variant for that race, else `<id>`.
 */
export function professionIconKey(
  def: { id: number; raceVariants?: { raceIds: number[] }[] } | undefined,
  professionId: number,
  race: number,
): string {
  const id = professionId | 0;
  if (race <= 0) return String(id);
  const hasVariant = def
    ? !!def.raceVariants?.some((v) => v.raceIds.includes(race))
    : !!PROFESSION_LABELS[id]?.[race];
  return hasVariant ? `${id}_${race}` : String(id);
}

/** Level-10 profession. Senior paths (tier 2) are not offered in the vow. */
export function isBaseProfession(def: {
  tier?: ProfessionTier;
  parentId?: number;
}): boolean {
  return (def.tier ?? 1) < 2 && !def.parentId;
}

/**
 * True when exclusive rules allow learning `node` given already-learned ability ids.
 * Within an exclusiveGroup, all learned nodes must share the same exclusiveKey.
 */
export function skillTreeExclusiveOk(
  tree: SkillTreeNodeDef[] | undefined,
  learnedAbilityIds: Iterable<string>,
  node: SkillTreeNodeDef,
): boolean {
  if (!node.exclusiveGroup || !node.exclusiveKey || !tree?.length) return true;
  const learned = new Set(learnedAbilityIds);
  for (const n of tree) {
    if (n.exclusiveGroup !== node.exclusiveGroup) continue;
    if (!learned.has(n.abilityId)) continue;
    if (n.exclusiveKey && n.exclusiveKey !== node.exclusiveKey) return false;
  }
  return true;
}

/** Prerequisites satisfied for learning rank 1 of `node`. */
export function skillTreePrereqsMet(
  tree: SkillTreeNodeDef[] | undefined,
  learnedAbilityIds: ReadonlySet<string> | Map<string, number>,
  node: SkillTreeNodeDef,
): boolean {
  const has = (abilityId: string): boolean => {
    if (learnedAbilityIds instanceof Map) return (learnedAbilityIds.get(abilityId) ?? 0) >= 1;
    return learnedAbilityIds.has(abilityId);
  };
  const abilityOf = (nodeId: string): string | undefined =>
    findTreeNodeById(tree, nodeId)?.abilityId;

  for (const req of node.requires ?? []) {
    const aid = abilityOf(req);
    if (!aid || !has(aid)) return false;
  }
  const any = node.requiresAny ?? [];
  if (any.length > 0) {
    let ok = false;
    for (const req of any) {
      const aid = abilityOf(req);
      if (aid && has(aid)) {
        ok = true;
        break;
      }
    }
    if (!ok) return false;
  }
  return true;
}

export interface ResolvedProfession {
  id: number;
  classId: number;
  label: string;
  description: string;
  abilityIds: string[];
  tree?: SkillTreeNodeDef[];
  variant?: ProfessionRaceVariant;
  kit?: CombatKitId;
}

export function resolveProfession(
  def: ProfessionOptionDef | undefined,
  race: number,
): ResolvedProfession | null {
  if (!def) return null;
  const normalized = normalizeProfessionEntry(def);
  const variant = normalized.raceVariants?.find((v) => v.raceIds.includes(race));
  return {
    id: normalized.id,
    classId: normalized.classId,
    label: variant?.label ?? normalized.label,
    description: variant?.description ?? normalized.description,
    abilityIds: variant?.abilityIds?.length
      ? variant.abilityIds.map((id) => String(id))
      : normalized.tree?.length
        ? abilityIdsVisibleForRace(normalized.tree, race)
        : normalized.abilityIds,
    tree: normalized.tree,
    variant,
    kit: variant?.kit ?? normalized.kit,
  };
}

/**
 * Fallback labels when the catalog is not loaded (leaderboard / offline).
 * Mirrors content/professions/catalog.json; key 0 = label without race.
 */
const PROFESSION_LABELS: Record<number, Record<number, string>> = {
  [ProfessionId.WaterMage]: { 0: "Маг воды", [Race.Human]: "Маг воды", [Race.DarkElf]: "Заклинатель бури" },
  [ProfessionId.Pyromancer]: {
    0: "Пиромант",
    [Race.Human]: "Пиромант",
    [Race.DarkElf]: "Маг Бездны",
    [Race.Orc]: "Шаман пламени",
  },
  [ProfessionId.EarthMage]: { 0: "Шаман камня", [Race.Orc]: "Шаман камня" },
  [ProfessionId.Gladiator]: {
    0: "Гладиатор",
    [Race.Human]: "Гладиатор",
    [Race.Dwarf]: "Молотоборец",
    [Race.Orc]: "Берсерк",
  },
  [ProfessionId.Knight]: {
    0: "Рыцарь",
    [Race.Human]: "Рыцарь",
    [Race.Dwarf]: "Страж-кузнец",
    [Race.Orc]: "Воевода",
  },
  [ProfessionId.Scout]: {
    0: "Охотник",
    [Race.Human]: "Охотник",
    [Race.LightElf]: "Танцующий с клинками",
    [Race.DarkElf]: "Сумеречный танцор",
  },
  [ProfessionId.Marksman]: {
    0: "Снайпер",
    [Race.Human]: "Снайпер",
    [Race.LightElf]: "Эльфийский лучник",
    [Race.DarkElf]: "Тёмный лучник",
  },
  [ProfessionId.Cleric]: {
    0: "Клирик",
    [Race.Human]: "Клирик",
    [Race.LightElf]: "Последователь Единого древа",
    [Race.Dwarf]: "Рунный жрец",
  },
  [ProfessionId.Bishop]: {
    0: "Епископ",
    [Race.Human]: "Епископ",
    [Race.LightElf]: "Хранитель природы",
    [Race.Dwarf]: "Жрец Горна",
  },
};

/** Fill the label table from the live catalog (senior paths and race names included). */
export function registerProfessionLabels(
  entries: readonly { id: number; label: string; raceVariants?: { raceIds: number[]; label: string }[] }[],
): void {
  for (const e of entries) {
    const row: Record<number, string> = { 0: e.label };
    for (const v of e.raceVariants ?? []) for (const r of v.raceIds) row[r] = v.label;
    PROFESSION_LABELS[e.id | 0] = row;
  }
}

export function professionLabel(id: number, race = 0): string {
  const row = PROFESSION_LABELS[id];
  if (!row) return "";
  return row[race] ?? row[0] ?? "";
}

/** Profession name when chosen, else base class label. */
export function classOrProfessionLabel(
  classId: number,
  professionId: number,
  classLabels: Record<number, string>,
  race = 0,
): string {
  const p = professionLabel(professionId, race);
  if (p) return p;
  return classLabels[classId] ?? `Класс ${classId}`;
}

/**
 * Leaderboard board=class scope_id (TINYINT):
 * - profession_id > 0 → profession_id
 * - else → BASE_CLASS_SCOPE + class_id (200+1… = Воин без профессии, …)
 * Keeps Gladiator (10) distinct from Warrior-without-job (201).
 */
export const LEADERBOARD_BASE_CLASS_SCOPE = 200;

export function leaderboardClassScope(classId: number, professionId: number): number {
  const p = professionId | 0;
  if (p > 0) return p;
  return LEADERBOARD_BASE_CLASS_SCOPE + (classId | 0);
}

export function leaderboardClassScopeLabel(
  scopeId: number,
  classLabels: Record<number, string>,
  race = 0,
): string {
  const s = scopeId | 0;
  if (s > LEADERBOARD_BASE_CLASS_SCOPE) {
    const classId = s - LEADERBOARD_BASE_CLASS_SCOPE;
    return classLabels[classId] ?? `Класс ${classId}`;
  }
  const p = professionLabel(s, race);
  if (p) return p;
  return `Профессия ${s}`;
}

/** Dropdown options for the class/profession leaderboard board. */
export function leaderboardClassScopeOptions(
  classLabels: Record<number, string>,
): Array<{ id: number; label: string }> {
  const profIds = [
    ProfessionId.Gladiator,
    ProfessionId.Knight,
    ProfessionId.WaterMage,
    ProfessionId.Pyromancer,
    ProfessionId.EarthMage,
    ProfessionId.Scout,
    ProfessionId.Marksman,
    ProfessionId.Cleric,
    ProfessionId.Bishop,
  ];
  const out: Array<{ id: number; label: string }> = [];
  for (const id of profIds) {
    const label = professionLabel(id);
    if (label) out.push({ id, label });
  }
  for (const [idStr, label] of Object.entries(classLabels)) {
    const classId = Number(idStr);
    if (!Number.isFinite(classId) || classId <= 0) continue;
    out.push({ id: LEADERBOARD_BASE_CLASS_SCOPE + classId, label });
  }
  return out;
}
